<template>
  <section>
    <question v-if="display"></question>
    <errors v-if="isVisibleErrorsModal"></errors>
    <commercial-address-override
      v-if="isCommercialOverrideModalVisible"
      @confirm="confirmCommercialOverride"
      @cancel="cancelCommercialOverride"></commercial-address-override>
    <accountCreation v-if="isAccountCreationModalVisible"></accountCreation>
    <address-suggestion v-if="isSuggestedAddressModalVisible"></address-suggestion>
    <merge-partial-user
      v-if="isMergePartialUserModalVisible"
      :closeAction="closeMergePartialUserModal"
      :partialUserId="user.id"
      @userMergeRequested="handleUserMerge"/>
    <div class="content-wrapper">

      <div class="content-heading">
        All users
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br />

      <div class="row">
        <spinner v-if="isLoading"></spinner>
        <div class="col-lg-12">

          <div id="panelChart9" class="panel panel-default">
            <div v-if="$route.query.retry" class="alert alert-warning" role="alert">
              <h4><em class="icon-exclamation pr-sm"></em>There was an issue validating your personal information.</h4>
              <p>Please verify your information and submit it once more.</p>
            </div>

            <div class="panel-title">
              User information
            </div>
            <div class="line-separator"></div>

            <div class="row bottom-margin">
              <div class="col-md-4">
                <div v-bind:class="getStyleClassForUserTypeRadioButton(userTypes.INVESTOR)">
                  <input type="radio" name="InvestorOption" id="InvestorOption" v-model="user.userType" value="INVESTOR"
                    v-bind:disabled="isFinancialAdvisorOrAdmin()" />
                  <label for="InvestorOption">Investor</label>
                </div>
              </div>
              <div class="col-md-4">
                <div v-bind:class="getStyleClassForUserTypeRadioButton(userTypes.FINANCIAL_ADVISOR)">
                  <input type="radio" name="FinancialAdvisorOption" id="FinancialAdvisorOption" v-on:click="selectAdvisor()"
                    disabled v-model="user.userType" value="ADVISOR" />
                  <label for="FinancialAdvisorOption">Financial Advisor </label>
                </div>
              </div>
            </div>
            <div class="row bottom-margin">
              <div class="col-md-4">
                <div v-bind:class="getStyleClassForUserTypeRadioButton(userTypes.MINOR)">
                  <input type="radio" name="MinorOption" id="MinorOption" v-model="user.userType" value="MINOR"
                    v-bind:disabled="isFinancialAdvisorAdminOrInstitutional()"/>
                  <label for="MinorOption">Minor</label>
                </div>
              </div>
              <div class="col-md-4">
                <div v-bind:class="getStyleClassForUserTypeRadioButton(userTypes.ADMIN)">
                  <input type="radio" name="AdminOption" id="AdminOption" disabled v-model="user.userType" value="ADMIN" />
                  <label for="AdminOption">Admin</label>
                </div>
              </div>
            </div>
            <div class="row bottom-margin">
              <div class="col-md-4">
                <div v-bind:class="getStyleClassForUserTypeRadioButton(userTypes.INSTITUTIONAL_REPRESENTATIVE)">
                  <input type="radio" name="InstitutionalRepresentativeOption" id="InstitutionalRepresentativeOption" v-model="user.userType" value="INSTITUTIONAL_REPRESENTATIVE" />
                  <label for="InstitutionalRepresentativeOption">Institutional Representative</label>
                </div>
              </div>
            </div>
            <div class="row bottom-margin">
              <div class="col-md-4">
                <div v-bind:class="getStyleClassForUserTypeRadioButton(userTypes.READ_ONLY)">
                  <input type="radio" name="ReadOnlyOption" id="ReadOnlyOption" v-model="user.userType" value="READ_ONLY" />
                  <label for="ReadOnlyOption">Read-only</label>
                </div>
              </div>
            </div>
            <div class="line-separator"></div>

            <div class="row">
              <div class="col-md-3">
                <div>
                  <label>Comment Category</label>
                </div>
              </div>
            </div>

            <div class="row bottom-margin">
              <div class="col-md-4">
                <div>
                  <multiselect
                    v-model="userCommentCategory"
                    :options="userCommentCategories"
                    :showToggleAll=false
                    placeholder="Select relevant categories"
                    optionLabel="name"
                  />
                </div>
              </div>
            </div>

            <div class="row bottom-margin">
              <div class="col-md-12">
                <span>Please append your comment below.</span>
                <span v-if="savingComment" class="auto-save-notice-label auto-save-notice-saving-color">[ Saving... ]</span>
                <span v-if="commentSaved" class="auto-save-notice-label auto-save-notice-saved-color">[ Saved ]</span>
                <span v-if="commentSaveError" class="auto-save-notice-label auto-save-notice-error-color">[ Not Saved ]</span>
                <restricted-textarea
                  :limit="255"
                  :input="userCommentInput"
                  id="comment"
                  :required="false"
                  :showNotice="false"
                  :showLimit="true"
                  :textColorClass="'vinous-text'"
                  placeholder=""
                  @inputUpdated="commentUpdated"
                />
              </div>
            </div>

          <basic-information
            :looseValidation="true"
            v-bind:basicInformation="user"
            :enableMfa="enableMfa"
            @emailUpdated="handleEmailChange"
            ref="basicInformation">
          </basic-information>
          <div v-if="showProvideLoginOption" class="checkbox c-checkbox check-margin">
            <label class="dark-grey-text">
              <input type="checkbox" value="" name="provide-investor-login" v-model="provideInvestorLogin" :checked="shouldBeCheckedByDefault" :disabled="!emailIsValid">
              <span class="fa fa-check"></span>
              Provide investor log in
            </label>
          </div>
          <div v-if="showProvideLoginOption" class="checkbox c-checkbox check-margin">
            <label class="dark-grey-text">
              <input type="checkbox" value="" name="enable-mfa" v-model="enableMfa" :checked="shouldBeCheckedByDefault" :disabled="!provideInvestorLogin">
              <span class="fa fa-check"></span>
              Enable MFA
            </label>
          </div>
          <div v-if="showLoginNotice" class="panel-body account-notice">
            <div class="alert alert-info alert-dismissible mt-lg" role="alert">
              <p>
                <em class="icon-info pr-sm"></em>
                Normally we do NOT provide minors with a log in.
              </p>
            </div>
          </div>
          <addresses-form
            v-if="user.residentialAddress && user.mailingAddress"
            v-bind:firstTitle="'Residential Address'"
            v-bind:residentialAddress="user.residentialAddress"
            v-bind:secondTitle="'Mailing Address'"
            v-bind:mailingAddress="user.mailingAddress"
            v-bind:key="1"
            v-bind:is-admin="true"
            v-bind:selected-user-type="user.userType"
            v-bind:shouldShowAllowPOBoxInput="true"
            v-bind:userHasBeenAllowedPOBox="userHasBeenAllowedPOBox"
            ref="addresses"
            ></addresses-form>

            <div class="row top-margin bottom-margin">
              <div class="col-md-2">
                <button type="submit" v-bind:disabled="disableSubmitButton()" class="btn btn-info btn-lg button-submit" v-on:click="validateAndSubmitUser()">Submit</button>
              </div>
            </div>

            <dismissable-alert
              title="You've changed the default account"
              subtitle=""
              :closeAction="hideDefaultAccountAlert"
              :isShowing="showDefaultAccountChanged"
            />
            <div class="panel-title">
              Accounts
            </div>
            <div class="line-separator"></div>
              <div v-if="user.accounts && user.accounts.length === 0" class="panel-body">
               <div class="alert alert-info alert-dismissible" role="alert">
                   No accounts found.
                </div>
            </div>
            <div v-if="user.accounts && user.accounts.length > 0" v-bind:class="{'table-fixed-header--sm cols-5': user.accounts.length > 3 || fetchingSummaryReports}"
              class="table-responsive bottom-margin">
              <spinner class="table-spinner" v-if="fetchingSummaryReports" ></spinner>
              <table class="table table-striped" v-if="!fetchingSummaryReports">
                <thead>
                  <tr>
                    <th class="text-center">Account name</th>
                    <th class="text-center">Type of account</th>
                    <th class="text-center">Account Balance</th>
                    <th class="text-center">Default Account</th>
                    <th class="text-center last-column-title">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="account in user.accounts" :key="account.id">
                    <td class="text-center">{{account.accountName}}</td>
                    <td class="text-center">{{account.accountType}}</td>
                    <td class="text-center">{{formatAmount(account.currentBalance)}}</td>
                    <td class="text-center">
                      <div
                        :class="{ 'radio-item': true, 'radio-item-unselected': account.id !== user.defaultAccountId }"
                      >
                        <input
                          type="radio"
                          :id="account.id"
                          :for="account.id"
                          :value="account.id"
                          @click="changeDefaultAccount(account)"
                        />
                        <label :for="account.id" class="lbl-defaultAccount" />
                      </div>
                    </td>
                    <td class="text-center last-column-data">
                      <button type="button" class="btn btn-details btn-outline" v-on:click="goToViewDetail(account)">View details</button>
                      <button type="button" class="btn btn-details btn-outline" v-on:click="generateSummaryReport(account)" >Summary Report</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="row">
              <div class="col-md-12 top-margin">
                <button type="submit" class="btn btn-default btn-lg btn-new"
                  v-on:click="openAccountCreationModal()">Create new</button>
                <button v-if="user.accounts && user.accounts.length === 0 && user.userType !== userTypes.INSTITUTIONAL_REPRESENTATIVE.name && user.userType !== userTypes.READ_ONLY.name" type="button" class="btn btn-warning btn-lg" v-on:click="mergePartialUser()">Merge partial user</button>
              </div>
            </div>

            <div v-if="showRecentActivity" class="line-separator top-margin"></div>

            <div v-if="showRecentActivity" class="panel-title">
              Recent activity
            </div>
            <div v-if="showRecentActivity" class="line-separator"></div>
            <div v-if="showRecentActivity" class="bottom-margin">
              <div v-if="!user.lastLogins || user.lastLogins.length === 0" class="panel-body">
                <div class="alert alert-info" role="alert">
                  No login activity found.
                </div>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th class="text-center">Last login</th>
                      <th class="text-center">Device</th>
                      <th class="text-center">IP address</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="login in user.lastLogins" :key="login.id">
                      <td class="text-center">{{ formatLoginTimestamp(login.loggedInAt) }}</td>
                      <td class="text-center">{{ login.device || '—' }}</td>
                      <td class="text-center">{{ login.ipAddress || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="showRecentActivity" class="line-separator top-margin"></div>

            <div class="panel-title">
              Investor feedback
            </div>
            <div class="line-separator"></div>
            <div class="bottom-margin">
              <spinner class="table-spinner" v-if="fetchingFeedback"></spinner>
              <div v-if="!fetchingFeedback && userFeedback.length === 0" class="panel-body">
                <div class="alert alert-info" role="alert">
                  No feedback submitted.
                </div>
              </div>
              <div v-if="!fetchingFeedback && userFeedback.length > 0" class="table-responsive">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th class="text-center">Date</th>
                      <th class="text-center">Score</th>
                      <th class="text-center">Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="feedback in userFeedback" :key="feedback.id">
                      <td class="text-center">{{ formatFeedbackDate(feedback.createdAt) }}</td>
                      <td class="text-center">{{ formatFeedbackScore(feedback.score) }}</td>
                      <td class="text-center feedback-comments">{{ feedback.reason || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="line-separator top-margin"></div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
  import {
    mapMutations,
    mapActions,
    mapState,
  } from 'vuex';
  import {
    SET_ERRORS_MODAL_INFO,
    SET_SUGGESTED_ADDRESS,
    SET_USER_BY_ADMIN,
    SET_SUGGESTED_ADDRESS_MODAL_VISIBLE,
    SET_SUGGESTED_ADDRESS_TYPE,
    SET_SUGGESTED_ADDRESS_YES_METHOD,
    SHOW_ERRORS_MODAL,
    SHOW_INFORMATION_MODAL,
    SHOW_QUESTION_MODAL,
    HIDE_QUESTION_MODAL,
    SHOW_ACCOUNT_CREATION_MODAL,
  } from '@/store/mutation-types';

  import MultiSelect from 'primevue/multiselect';
  import { orderBy, debounce } from 'lodash';
  import datePicker from 'vue-bootstrap-datetimepicker';
  import addressSuggestion from '@/components/modals/AddressSuggestion';
  import moment from 'moment';

  import errors from '@/components/modals/Errors';
  import commercialAddressOverride from '@/components/modals/CommercialAddressOverride';
  import information from '@/components/modals/Information';
  import spinner from '@/components/widgets/Spinner';
  import Question from '@/components/modals/Question';
  import DismissableAlert from '@/components/widgets/DismissableAlert';
  import accountCreation from '@/components/modals/AccountCreation';
  import MergePartialUser from '@/components/modals/MergePartialUser';
  import addresses from '@/components/Addresses';
  import basicInformation from '@/components/BasicInformation';
  import { UserTypes } from '@/data/static/userTypes';
  import { AddressErrors } from '@/data/static/addressErrors';
  import breadcrumb from '@/components/Breadcrumb';
  import RestrictedTextArea from '@/components/formControls/RestrictedTextArea';

  import {
    formatDate,
    getAccounTypeProperties,
    formatAmount,
    downloadPDF,
  } from '@/util';
  import {
    Constants,
  } from '@/data/static/constants';

  const mix = {
    methods: {
      formatDate,
      formatAmount,
    },
  };

  export default {
    name: 'UserDetail',
    beforeMount() {
      this.initializeState();
      this.loadUserDetail();
      this.loadUserFeedback();
    },
    watch: {
      async suggestedAddressModalYesCall({ payload, suggestedAddress, addressType }) {
        if (!this.$refs.addresses.isMailingAddressDifferent) {
          this.user.residentialAddress = suggestedAddress;
          this.user.mailingAddress = suggestedAddress;
          this.$refs.addresses.setResidencialAddress(suggestedAddress);
          this.$refs.addresses.setMailingAddress(suggestedAddress);
        }
        if (['residential', 'organization'].includes(addressType)) {
          this.user.residentialAddress = suggestedAddress;
          this.$refs.addresses.setResidencialAddress(suggestedAddress);
        } else if (addressType === 'mailing') {
          this.user.mailingAddress = suggestedAddress;
          this.$refs.addresses.setMailingAddress(suggestedAddress);
        }
        await this.save({
          ...payload,
          residentialAddress: this.user.residentialAddress,
          mailingAddress: this.user.mailingAddress,
        });
      },
      userCommentCategory() {
        if (!this.userCommentCategoriesHasChanged) return;
        this.saveComments();
      },
      provideInvestorLogin(provideInvestorLogin) {
        if (!provideInvestorLogin) {
          this.enableMfa = false;
        }
      },
      enableMfa(newValue) {
        // Clear MFA phone preference when MFA is disabled
        if (!newValue && this.$refs.basicInformation) {
          this.$refs.basicInformation.mfaPhonePreference = null;
        }
      },
      user: function changedUser(newUser) {
        this.defaultAccountId = newUser.defaultAccountId;
        this.breadcrumbData = [];
        this.breadcrumbData.push(
          Object.assign({}, {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          }),
        );
        const title = newUser.firstName.concat(' ', newUser.lastName);
        const goto = { name: this.$route.name, params: Object.assign({}, this.$route.params) };
        this.breadcrumbData.push(
          Object.assign({}, {
            title,
            goto,
            selected: true,
          }),
        );
      },
    },
    mixins: [mix],
    data() {
      return {
        userId: '',
        minimumAge: 18,
        breadcrumbData: [],
        user: {},
        defaultAccountId: null,
        showDefaultAccountChanged: false,
        isLoading: false,
        creationSucceeded: false,
        fetchingSummaryReports: false,
        userTypes: UserTypes,
        provideInvestorLogin: false,
        enableMfa: false,
        emailIsValid: false,
        userHasBeenAllowedPOBox: false,
        isCommercialOverrideModalVisible: false,
        pendingCommercialOverridePayload: null,
        userCommentCategory: [],
        userCommentInput: '',
        savingComment: false,
        commentSaved: false,
        commentSaveError: false,
        isMergePartialUserModalVisible: false,
        userFeedback: [],
        fetchingFeedback: false,
      };
    },
    computed: {
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('accountCreation', [
        'isSuggestedAddressModalVisible',
        'isAccountCreationModalVisible',
        'changeAddress',
        'suggestedAddressModalYesCall',
      ]),
      ...mapState('information', [
        'isInformationModalVisible',
      ]),
      ...mapState('question', [
        'display',
      ]),
      ...mapState('app', {
        userCommentCategories: state => state.userCommentCategories,
        loggedUser: state => state.user,
      }),
      userCommentCategoriesHasChanged() {
        return this.user.commentCategories !== this.userCommentCategory;
      },
      userCommentsHasChanged() {
        return this.user.comments !== this.userCommentInput;
      },
      showProvideLoginOption() {
        return this.user.userType === this.userTypes.INVESTOR.name
          || this.user.userType === this.userTypes.MINOR.name
          || this.user.userType === this.userTypes.INSTITUTIONAL_REPRESENTATIVE.name
          || this.user.userType === this.userTypes.READ_ONLY.name;
      },
      showLoginNotice() {
        return this.user.userType === this.userTypes.MINOR.name && this.provideInvestorLogin;
      },
      shouldBeCheckedByDefault() {
        return this.provideInvestorLogin || this.user.clientId !== null;
      },
      showRecentActivity() {
        return true;
      },
    },
    methods: {
      formatLoginTimestamp(date) {
        if (!date) return '----------';
        return moment(date).format('MM/DD/YYYY h:mm A');
      },
      async loadUserFeedback() {
        this.fetchingFeedback = true;
        await this.getUserFeedback(this.userId)
          .then((data) => {
            this.userFeedback = orderBy(data || [], ['createdAt'], ['desc']);
          })
          .catch((error) => {
            // Feedback is supplemental, so a failure here must not block the page.
            console.error('Error fetching user feedback:', error);
            this.userFeedback = [];
          });
        this.fetchingFeedback = false;
      },
      formatFeedbackDate(date) {
        if (!date) return '----------';
        return moment(date).format('MM/DD/YYYY h:mm A');
      },
      formatFeedbackScore(score) {
        // Out of 10, so the number reads as a rating rather than a count.
        return score === null || score === undefined ? '—' : `${score}/10`;
      },
      setSavingComment() {
        this.savingComment = true;
        this.commentSaved = false;
        this.commentSaveError = false;
      },
      setCommentSaved() {
        this.savingComment = false;
        this.commentSaved = true;
        this.commentSaveError = false;
      },
      setCommentSaveError() {
        this.savingComment = false;
        this.commentSaved = false;
        this.commentSaveError = true;
      },
      saveComments() {
        this.setSavingComment();
        const payload = {
          comments: this.userCommentInput,
          commentCategories: this.userCommentCategory.map(c => c.id),
        };

        this.addUserComments({ userId: this.user.id, payload })
        .then(() => {
          this.user = Object.assign(
            this.user, { comments: this.userCommentInput },
          );
          this.setCommentSaved();
        })
        .catch(() => {
          this.setCommentSaveError();
        });
      },
      debouncedSave: debounce(function fn() {
        this.saveComments();
      }, 800),
      commentUpdated($event) {
        this.setSavingComment();
        this.userCommentInput = $event;
        if (!this.userCommentsHasChanged) return;

        this.debouncedSave();
      },
      getHasBeenAllowedPOBoxValue() {
        return this.$refs.addresses.hasBeenAllowedPOBox;
      },
      hideDefaultAccountAlert() {
        this.showDefaultAccountChanged = false;
      },
      changeDefaultAccount(account) {
        if (this.loggedUser &&
        this.loggedUser.userType === UserTypes.READ_ONLY.name &&
        this.user && this.user.id !== this.loggedUser.id) {
          return;
        }
        this.showQuestionModal({
          title: 'Default Account',
          question: `Are you sure you want to select ${account.accountName} as new default account?`,
          method: () => {
            this.confirmChangeDefaultAccount(account.id);
          },
          yesButtonText: Constants.purchaseNote.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      async confirmChangeDefaultAccount(id) {
        if (this.loggedUser &&
          this.loggedUser.userType === UserTypes.READ_ONLY.name &&
          this.user &&
          this.user.id !== this.loggedUser.id) {
          return;
        }
        this.isLoading = true;
        try {
          await this.setDefaultAccount({
            accountId: id,
            userId: this.user.id,
          });
          this.hideQuestionModal();
          await this.loadUserDetail();
          this.isLoading = false;
          this.showDefaultAccountChanged = true;
        } catch (error) {
          this.isLoading = false;
        }
      },
      handleEmailChange($event) {
        this.emailIsValid = $event.valid;
        if (!this.emailIsValid) {
          this.provideInvestorLogin = false;
        }
      },
      ...mapActions('userCreationByAdmin', [
        'updateUserProfile',
        'updateUser',
      ]),
      ...mapActions('user', [
        'addUserComments',
      ]),
      ...mapActions('feedback', [
        'getUserFeedback',
      ]),
      ...mapActions('account', ['getAccountSummaryReport', 'getDocumentFromS3']),
      ...mapActions('accountCreation', [
        'setDefaultAccount',
        'getUserDetail',
      ]),
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
        showQuestionModal: SHOW_QUESTION_MODAL,
      }),
      ...mapMutations('information', {
        showInformationModal: SHOW_INFORMATION_MODAL,
      }),
      ...mapMutations('accountCreation', {
        setSuggestedAddress: SET_SUGGESTED_ADDRESS,
        setUserByAdmin: SET_USER_BY_ADMIN,
        setSuggestedAddressType: SET_SUGGESTED_ADDRESS_TYPE,
        setSuggestedAddressModalVisible: SET_SUGGESTED_ADDRESS_MODAL_VISIBLE,
        setSuggestedAddressYesMethod: SET_SUGGESTED_ADDRESS_YES_METHOD,
        showAccountCreationModal: SHOW_ACCOUNT_CREATION_MODAL,
      }),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      initializeState() {
        this.userId = this.$route.params.userId;
        this.name = this.$route.params.name;
      },
      async loadUserDetail() {
        this.isLoading = true;
        await this.getUserDetail(this.userId)
          .then((data) => {
            this.user = Object.assign({}, {
              ...data,
              commentCategories: (data.commentCategories || []).map(
                c => ({
                  id: c.commentCategory.id,
                  name: c.commentCategory.name,
                }),
              ),
            });
            this.sortAccounts();
            this.userCommentCategory = this.user.commentCategories;
            this.userCommentInput = data.comments;
            this.userHasBeenAllowedPOBox = data.hasBeenAllowedPOBox;
            this.provideInvestorLogin = Boolean(data.clientId);
            this.enableMfa = data.isMfaEnabled;
            this.isLoading = false;
          })
          .catch((error) => {
            console.error('Error fetching user detail:', error);
            this.isLoading = false;
          });
      },
      sortAccounts() {
        this.user.accounts = orderBy(this.user.accounts, [(account) => {
          const name = account.accountName;
          return name ? account.accountName.toLowerCase() : '';
        }], ['asc']);
      },
      updateAddress(originalAddress, updatedAddress) { /* eslint-disable no-param-reassign */
        originalAddress.streetAddress1 = updatedAddress.streetAddress1;
        originalAddress.streetAddress2 = updatedAddress.streetAddress2;
        originalAddress.zipCode = updatedAddress.zipCode;
        originalAddress.state = updatedAddress.state;
        originalAddress.city = updatedAddress.city;
      },
      validateAndSubmitUser() {
        this.isLoading = true;
        this.setUserByAdmin(this.user);
        let request = {
          id: this.user.id,
        };
        if (this.$refs.basicInformation) {
          request = Object.assign(request, this.$refs.basicInformation.getData());
        }
        if (this.$refs.addresses) {
          request = Object.assign(request, this.$refs.addresses.getData());

          this.user.residentialAddress = Object.assign(this.user.residentialAddress,
          request.residentialAddress);
          this.$refs.addresses.setResidencialAddress(request.residentialAddress);
          this.user.mailingAddress = Object.assign(this.user.mailingAddress, request.mailingAddress);
          this.$refs.addresses.setMailingAddress(request.mailingAddress);
        }

        return this.showConfirmationModal(request);
      },
      async save(payload, commercialOverrideComment) {
        this.hideQuestionModal();
        try {
          const userData = {
            ...payload,
            hasBeenAllowedPOBox: this.getHasBeenAllowedPOBoxValue(),
            hasBeenAllowedCommercialAddress: !!commercialOverrideComment,
            commercialAddressOverrideComment: commercialOverrideComment || undefined,
          };

          await this.updateUserProfile({
            user: userData,
            provideLogin: this.provideInvestorLogin,
            enableMfa: this.enableMfa,
          });

          await this.loadUserDetail();
        } catch (err) {
          this.isLoading = false;
          this.user.socialSecurityNum = this.previousSSN;

          // A commercial residential address can be overridden by an admin via
          // a dedicated modal (instead of a global checkbox). Re-confirmed on
          // each save, so we don't auto-resend a stored flag.
          if (err && err.errorMessage === 'RESIDENTIAL_COMMERCIAL_ADDRESS_NOT_ALLOWED') {
            this.pendingCommercialOverridePayload = payload;
            this.isCommercialOverrideModalVisible = true;
            return;
          }

          // The API returns address validation errors as `errorMessage` (an
          // error code we map to a friendly description); other errors use
          // `message`. Reading the wrong field showed admins a generic message
          // instead of the real reason.
          const rawError = (err && (err.errorMessage || err.message)) || '';
          const errorMessage = AddressErrors[rawError] || rawError ||
            'An error occurred while saving the user profile. Please try again.';
          this.setErrorsModalInfo([errorMessage]);
          this.showErrorsModal();
        }
      },
      confirmCommercialOverride(comment) {
        this.isCommercialOverrideModalVisible = false;
        const payload = this.pendingCommercialOverridePayload;
        this.pendingCommercialOverridePayload = null;
        this.isLoading = true;
        this.save(payload, comment);
      },
      cancelCommercialOverride() {
        this.isCommercialOverrideModalVisible = false;
        this.pendingCommercialOverridePayload = null;
      },
      showUserModalsError(listUserErrors) {
        this.setErrorsModalInfo(listUserErrors);
        this.showErrorsModal();
      },
      disableSubmitButton() {
        const userType = this.user.userType;
        if (userType === UserTypes.ADMIN.name || userType === UserTypes.FINANCIAL_ADVISOR.name) {
          return true;
        }
        // READ_ONLY users cannot submit updates for other users
        if (this.loggedUser &&
          this.loggedUser.userType === UserTypes.READ_ONLY.name &&
          this.user && this.user.id !== this.loggedUser.id) {
          return true;
        }
        let basicInformationValid = false;
        let addressesValid = false;

        if (this.$refs.addresses) {
          addressesValid = this.$refs.addresses.validate();
          if ((userType === UserTypes.INSTITUTIONAL_REPRESENTATIVE.name
          || userType === UserTypes.READ_ONLY.name) &&
            (
              this.$refs.addresses.areEmpty() ||
              (
                !this.$refs.addresses.isMailingAddressDifferent &&
                (
                  this.$refs.addresses.isResidentialAddressEmptyOrValid() &&
                    this.$refs.addresses.isMailingAddressEmptyOrValid()
                )
              )
            )
          ) {
            addressesValid = true;
          }
        }
        if (this.$refs.basicInformation) {
          basicInformationValid = this.$refs.basicInformation.validate();
          if (
            basicInformationValid &&
              this.$refs.basicInformation.getData().email === '' &&
              (userType === UserTypes.INSTITUTIONAL_REPRESENTATIVE.name
                || userType === UserTypes.READ_ONLY.name)
          ) {
            basicInformationValid = false;
          }
        }
        return !basicInformationValid || !addressesValid
          || this.isLoading;
      },
      showConfirmationModal(userToUpdate) {
        this.isLoading = false;
        this.showQuestionModal({
          title: Constants.userDetail.modalTitle,
          question: !this.getHasBeenAllowedPOBoxValue() ?
            Constants.userDetail.modalQuestion : Constants.userDetail.modalQuestionPOBox,
          method: () => this.save(userToUpdate),
          yesButtonText: Constants.userDetail.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      openAccountCreationModal() {
        const userId = this.userId;
        const name = this.name;
        this.showAccountCreationModal({ userId, name });
      },
      mergePartialUser() {
        this.isMergePartialUserModalVisible = true;
      },
      closeMergePartialUserModal() {
        this.isMergePartialUserModalVisible = false;
      },
      handleUserMerge({ targetUserId }) {
        // Close the merge modal first to prevent z-index issues
        this.closeMergePartialUserModal();
        // Show confirmation modal
        this.showQuestionModal({
          title: 'Merge Partial User',
          question:
            'Are you sure you want to merge this partial user? This action cannot be undone.',
          method: () => {
            this.executeMerge(targetUserId);
          },
          yesButtonText: 'Confirm',
          hideCancelButton: false,
        });
      },
      async executeMerge(targetUserId) {
        this.hideQuestionModal();
        this.isLoading = true;
        try {
          await this.$store.dispatch('user/mergePartialUser', {
            partialUserId: this.user.id,
            targetUserId,
          });

          // Success - redirect to the search page
          this.$router.push({ name: 'Users' });
        } catch (error) {
          console.error('Error merging users:', error);
          const errorMessage =
            (error && error.response && error.response.data && error.response.data.message) ||
            'Failed to merge users. Please try again.';
          this.setErrorsModalInfo([errorMessage]);
          this.showErrorsModal();
        } finally {
          this.isLoading = false;
        }
      },
      async generateSummaryReport(account) {
        this.fetchingSummaryReports = true;

        const responseWithoutAccrued = await this.getAccountSummaryReport({
          accountId: account.id,
          withAccruedInterest: false,
        });
        const htmlWithoutAccrued = await this.getDocumentFromS3({
          presignedUrl: responseWithoutAccrued.url,
        });

        downloadPDF(responseWithoutAccrued.filename, htmlWithoutAccrued.data);

        const responseWithAccrued = await this.getAccountSummaryReport({
          accountId: account.id,
          withAccruedInterest: true,
        });
        const htmlWithAccrued = await this.getDocumentFromS3({
          presignedUrl: responseWithAccrued.url,
        });

        downloadPDF(responseWithAccrued.filename, htmlWithAccrued.data);

        this.fetchingSummaryReports = false;
      },
      goToViewDetail(account) {
        const accountTypeProperties = getAccounTypeProperties(account.accountType);
        const routeParams = {
          name: accountTypeProperties.component,
          params: {
            accountId: account.id,
            name: this.name,
            userId: this.userId,
          },
        };

        this.$router.push(routeParams);
      },
      getStyleClassForUserTypeRadioButton(type) {
        return this.user.userType === type.name
          ? 'radio-item' : 'radio-item radio-item-unselected';
      },
      isFinancialAdvisorAdminOrInstitutional() {
        return this.user.userType === UserTypes.FINANCIAL_ADVISOR.name
          || this.user.userType === UserTypes.ADMIN.name
          || this.user.userType === UserTypes.INSTITUTIONAL_REPRESENTATIVE.name
          || this.user.userType === UserTypes.READ_ONLY.name;
      },
      isFinancialAdvisorOrAdmin() {
        return this.user.userType === UserTypes.FINANCIAL_ADVISOR.name
          || this.user.userType === UserTypes.ADMIN.name;
      },
    },
    components: {
      datePicker,
      errors,
      spinner,
      information,
      accountCreation,
      breadcrumb,
      question: Question,
      multiselect: MultiSelect,
      'dismissable-alert': DismissableAlert,
      'address-suggestion': addressSuggestion,
      'addresses-form': addresses,
      'basic-information': basicInformation,
      'restricted-textarea': RestrictedTextArea,
      'merge-partial-user': MergePartialUser,
      'commercial-address-override': commercialAddressOverride,
    },
  };
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .auto-save-notice-label {
    margin-left: 6px;
    font-weight: bold;
    font-size: 12px;
  }

  .auto-save-notice-saving-color {
    color: rgb(19, 134, 151);
  }

  .auto-save-notice-saved-color {
    color: rgb(13, 149, 22);
  }

  .auto-save-notice-error-color {
    color: rgb(196, 62, 0);
  }

  .restricted-textarea {
    height: 90px !important;
  }

  .message-feedback {
    background-color: #dbe9db;
    margin-left: 2%;
    margin-top: 2%;
    margin-bottom: 2%;
    width: 66%;
  }

  .birth-field-bottom {
    margin-bottom: 5px;
  }

  .button-submit {
    background-color: #127285;
  }

  .date-pick-style {
    width: 90%;
  }

  .content-wrapper {
    border-top: none;
  }

  /* No widths: the columns spread themselves the way the recent activity
     table above does. Only the wrapping is set, so a long comment breaks
     inside its cell instead of stretching the row. */
  .feedback-comments {
    white-space: pre-wrap;
    word-break: break-word;
  }

  .warning-heading {
    background-color: #f9f2ce !important;
    padding: 10px;
    margin: 0 auto 20px;
    font-size: 14px;
    font-weight: 500;
  }

  .btn-back {
    color: #31b0d5;
    background-color: Transparent;
    background-repeat: no-repeat;
    border: none;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    padding-left: 20px;
  }

  .icon-back {
    position: relative;
  }

  .icon-back:before {
    content: "\f060";
    font-family: FontAwesome;
    position: absolute;
    left: 0px;
  }

  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }

  .left-margin {
    margin-left: 4%;
  }

  .right-margin {
    margin-right: 4%;
  }

  .top-margin {
    margin-top: 2%;
  }

  .bottom-margin {
    margin-bottom: 2%;
  }

  .space {
    margin-top: 4%;
  }

  .content-heading {
    font-weight: normal;
  }

  .upload-container {
    height: 80px;
    padding-top: 10px;
  }

  .panel-title {
    font-size: 20px;
  }

  .panel-subtitle {
    font-size: 16px;
  }

  .panel-default {
    padding: 30px 40px;
  }

  .panel-heading {
    background-color: #ffffff;
    font-size: 16px;
    padding: 0px 5px;
  }

  label {
    color: #888;
    font-size: 14px;
    width: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0px;
  }

  .lbl-defaultAccount {
    color: #888;
    font-size: 14px;
    width: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0px;
  }

  select:required:invalid {
    color: lightgray;
  }

  option[value=""][disabled] {
    display: none;
  }

  option {
    color: black;
  }

   ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: lightgray;
    opacity: 1;
    /* Firefox */
  }

   :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: lightgray;
  }

   ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: lightgray;
  }

  .disabled-state {
    color: #ccc;
  }

  .text-red {
    color: #a94442;
  }

  .btn-new {
    margin-right: 8px;
  }

  .table-fixed-header--sm {
    height: 240px;
  }

  .table-fixed-header--sm tbody {
    height: 176px;
  }

  .radio-item {
    height: 25px;
    padding: 0;
  }

  .radio-item label {
    color: #666;
    font-weight: normal;
    padding-left: 25px;
    padding-top: 5px;
    cursor: pointer;
  }

  .radio-item label:before {
    content: ' ';
    display: inline-block;
    position: absolute;
    top: 5px;
    left: 0px;
    margin: 0 5px 0 0;
    width: 20px;
    height: 20px;
    border-radius: 11px;
    background-color: transparent;
  }

  .radio-item-unselected label:before {
    border: 2px solid #d3d3d3;
  }

  .radio-item label:after {
    border-radius: 5px;
    width: 10px;
    height: 10px;
    position: absolute;
    top: 10px;
    left: 0;
    content: ' ';
    display: block;
    background-color: #127285;
    margin-left: 5px;
  }

  .radio-item.radio-item-unselected label:after {
    display: none;
  }

  .dark-grey-text {
    color: #888888 !important;
    font-weight: bold;
    user-select: none;
  }

  .account-notice {
    padding: 0;
  }

  .last-column-data {
    width: 9%;
  }

  .last-column-title {
    width: 29%;
  }

  .table-spinner {
    position: relative;
  }
</style>



// WEBPACK FOOTER //
// UserDetail.vue?b343522e